/**
 * VirtualList - Lightweight virtual scrolling component
 * 
 * Uses measurement-based virtualization for optimal performance with variable-height items:
 * - Renders initial window (3x viewport by default) - only ~5-10 items initially
 * - Measures actual item heights as they render using ResizeObserver
 * - Uses default height (600px) for unmeasured items
 * - Replaces off-screen items with spacers using measured or default heights
 * - Progressively learns actual heights as you scroll
 * 
 * Features:
 * - No external dependencies
 * - Supports variable-height items (portraits, landscapes, expandable text)
 * - Smooth scrolling without jumps
 * - Automatic height tracking and updates
 * - No feedback loops or scroll compensation needed
 * - Memory efficient: only renders visible + buffer items
 */

import React, { useRef, useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import type { CSSProperties } from 'react';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import { useReversedAnchor, STICKY_BOTTOM_TOLERANCE_PX } from './useReversedAnchor';

/**
 * Configuration for grid rendering mode.
 * When passed as `gridProps` to VirtualList, the component renders a virtualized grid.
 * When absent, VirtualList renders in list mode.
 *
 * CELL CONTRACT — grid rows are a FIXED height, derived from the container
 * width, `columnsCount`, `crossAxisSpacing` and `itemAspectRatio` (see
 * `gridRowHeight`), and every cell box is `overflow: hidden`. The row height is
 * therefore whatever the geometry yields, NOT whatever the cell content wants:
 * virtualization (item positions, the `initialIndex` landing, spacer heights)
 * assumes exact, uniform row heights, so a row can never grow to fit its
 * content.
 *
 * Consequently **cell content must be able to fit an arbitrarily short cell**.
 * Content with a large intrinsic minimum — the classic case being a fixed-size
 * image stacked above a text row — needs at least one shrinkable element with
 * `minHeight: 0` (and the grid item itself allowed to be shorter than its
 * min-content height), or it overflows downward and is trimmed at the bottom.
 * Reference cell: `presentation/feature/gift/components/GiftGridItem.tsx`,
 * which pins its text row and shrinks its (aspect-ratio-locked) image box.
 */
export interface GridProps {
    /** Number of columns. Must be > 1. */
    columnsCount: number;

    /** Horizontal gap between columns in pixels. Default: 0. */
    crossAxisSpacing?: number;

    /** Vertical gap between rows in pixels. Default: 0. */
    mainAxisSpacing?: number;

    /**
     * Fixed aspect ratio (width / height) for grid items.
     * Used to compute fixed row height from container width.
     * Default: CONTENT_CONSTANTS.gridLayout.itemAspectRatio
     *
     * A SMALLER ratio makes rows taller; a larger one makes them shorter and is
     * the usual way a grid starts clipping its cells. Pair any increase with the
     * cell contract documented on `GridProps` above — the cell must degrade
     * gracefully, because the row will not grow.
     */
    itemAspectRatio?: number;

    /**
     * Number of extra rows to render above and below the visible rows as a scroll buffer.
     * Default: 5.
     */
    bufferRows?: number;

    /** Padding inside the grid container in pixels. Default: 0. */
    padding?: number;
}

/**
 * Normalises a `getItemKey(...)` result into the key type used by the
 * measured-height cache (`itemHeights`).
 *
 * WHY: `getItemKey` is typed `string | number`, and several callers legitimately
 * return a `number` (entity ids, enum ordinals — e.g. `LegalDocumentsPage`
 * returns `LegalDocumentItemType`). The measurement `ResizeObserver` can only
 * recover an item's key from the DOM via `element.getAttribute('data-key')`,
 * which is ALWAYS a string. Without a shared normalisation the cache is written
 * under `'0'` and read under `0` — `Map` uses SameValueZero, so those are
 * different entries and the measurement is never read back. Every item then
 * stays budgeted at `defaultItemHeight` (600px by default) forever, which
 * (a) truncates the render window in `updateVisibleRange` — a real, observed bug
 * where the last row of a 7-row list could never stay mounted — and
 * (b) makes the stale-key eviction pass delete every measured height on each
 * items change (the eviction `Set` held numbers, the cache held strings).
 *
 * Every read, write, and eviction against `itemHeights` MUST go through this
 * helper so both sides of the cache agree on the key type.
 */
const toHeightCacheKey = (key: string | number): string => String(key);

/**
 * Breathing-room gap (px) left above a scroll landing target, so the item the
 * list scrolls to (`scrollToIndex` / `initialIndex`) does not sit flush against
 * the top edge of the scroll viewport.
 *
 * TODO @Claude: Where this 16px top padding comes from? Should check if top
 * padding is set? (Pre-existing question — the value is unchanged from when it
 * was inlined as a literal at each landing site; it is deliberately NOT a
 * `responsive.*` token, because every `responsive.spacing` step that equals 16
 * on one breakpoint is a different number on another, which would silently move
 * the landing position on the pages that scroll correctly today.)
 *
 * Page chrome that overlays the top of that viewport (a sticky app bar) is NOT
 * part of this constant — callers declare it per-instance via
 * `VirtualListProps.scrollOffsetTop`.
 */
const LANDING_TOP_SPACING = 16;

/** Handle type for imperative methods */
export interface VirtualListHandle {
    /** Scroll to a specific item by index */
    scrollToIndex: (index: number, behavior?: ScrollBehavior) => void;

    /**
     * Scroll the resolved scroll element to the bottom of its content.
     *
     * Intended for chat-style (`reversed`) usage where the caller appends a
     * new message and wants the viewport to deterministically follow,
     * regardless of whether the user was previously at the bottom or had
     * scrolled up. Uses the same scroll-element resolution as
     * `scrollToIndex` (the `scrollContainer` prop if provided, otherwise the
     * nearest scrollable ancestor of the inner container, otherwise
     * `window`).
     *
     * For non-reversed (top-anchored) lists this is still a valid call —
     * it scrolls to `scrollHeight - clientHeight` (bottom of the rendered
     * content) — but the more typical "go to bottom" semantics in
     * top-anchored mode is `scrollToIndex(items.length - 1)`.
     *
     * Default `behavior`: `'auto'` (instant). Pass `'smooth'` for animated
     * scrolling.
     */
    scrollToBottom: (behavior?: ScrollBehavior) => void;

    /** Get the current scroll position of an item (if measured) */
    getItemPosition: (index: number) => number | null;

    /** Get currently visible viewport range */
    getViewportRange: () => { start: number; end: number };

    /** Get currently rendered range */
    getRenderedRange: () => { start: number; end: number };
}

export interface VirtualListProps<T> {
    /** Array of items to render */
    items: T[];

    /** Render function for each item */
    renderItem: (item: T, index: number) => React.ReactNode;

    /** Unique key extractor */
    getItemKey: (item: T, index: number) => string | number;

    /** Initial render window size (multiplier of viewport height). Default: 3 */
    initialWindowSize?: number;

    /**
     * Default height for unmeasured items (in pixels). Default: 600.
     *
     * Used by the spacer math to budget vertical space for items that have
     * not yet been measured by the ResizeObserver. In `reversed` mode this
     * value is also used to size the initial render window (see the
     * `reversed` JSDoc tuning note), but the **first-mount bottom-anchor**
     * does NOT depend on it being an accurate average — after the initial
     * snap, the effect re-snaps until `scrollHeight` settles, so tall
     * items (e.g. 150–200px bubbles) under a small `defaultItemHeight`
     * (e.g. 48px) still land at the true bottom of the list.
     */
    defaultItemHeight?: number;

    /** Custom className for the container */
    className?: string;

    /** Custom style for the container */
    style?: CSSProperties;

    /** Gap between items (in pixels) */
    gap?: number;

    /** Layout direction */
    direction?: 'vertical' | 'horizontal';

    /** Whether the list is currently active/visible (pause updates when inactive) */
    isActive?: boolean;

    /** Enable periodic updates every second as a safety mechanism (default: false) */
    enablePeriodicUpdates?: boolean;

    /** Callback when visible items change */
    onVisibleItemsChange?: (
        visibleItems: T[],
        visibleStartIndex: number,
        visibleEndIndex: number,
        renderedItems: T[],
        renderedStartIndex: number,
        renderedEndIndex: number
    ) => void;

    /**
     * Initial index to scroll to after mount and measurement.
     *
     * **Performance Warning**: When provided, items from index 0 up to initialIndex + small buffer
     * will be rendered off-screen during initial mount to measure their actual heights. This ensures
     * precise scroll positioning for items with variable heights (e.g., portrait/landscape videos
     * with different description lengths). After measurement and scroll, normal virtualization resumes.
     *
     * Only items BEFORE the target index need to be measured for accurate positioning.
     *
     * Trade-off:
     * - ✅ Accurate scroll position
     * - ✅ Only renders items up to target index (not entire list)
     * - ❌ Initial render cost proportional to initialIndex
     * - ❌ Higher memory usage during measurement phase
     *
     * Use when scroll accuracy is critical. Performance impact scales with initialIndex value.
     * In grid mode, no measurement phase occurs — scroll position is computed arithmetically.
     */
    initialIndex?: number;

    /**
     * Height (px) of sticky/fixed page chrome that overlays the TOP of this
     * list's scroll viewport — e.g. the page's `ExpauseAppBar`.
     *
     * Added to the landing offset of BOTH scroll-landing paths
     * (`scrollToIndex` and the `initialIndex` mount scroll) so the target item
     * lands BELOW the chrome instead of underneath it. Without it a landing
     * puts the target's top edge at viewport y = `LANDING_TOP_SPACING`, which
     * predates page chrome entirely: on a page with a 48px + safe-area sticky
     * bar the first rows of the target (on a content list: the author row)
     * arrive hidden behind the bar.
     *
     * Pass the chrome's MEASURED height (`useMeasuredHeight`), not a recomputed
     * `topBar.height + safeArea.top`: the bar's height folds in
     * `responsive.safeArea.top`, a unit-bearing `env()` STRING that cannot be
     * added to a number (`presentation.md` → Responsive).
     *
     * Applies to the window scroll and to a local `HTMLElement` scroll
     * container alike — the caller decides, because only the caller knows
     * whether its chrome sits inside the scrolled box (a sticky bar above the
     * list, which overlays it) or outside it (a scroll container that starts
     * below the bar, which does not). Leave unset in the latter case.
     *
     * Default: 0 — every existing caller keeps its current landing position.
     */
    scrollOffsetTop?: number;

    // ---- Grid layout props ----
    // When gridProps is provided, the list enters grid mode: items are grouped into fixed-height rows
    // and virtualization becomes row-based. When absent, the component renders in list mode.

    /** Background color of the grid container (grid mode only) */
    backgroundColor?: string;

    /**
     * Grid layout configuration. When provided, renders as a virtualized grid.
     * When absent, renders as a virtualized list.
     */
    gridProps?: GridProps;

    /**
     * Optional ref to a scrollable ancestor element.
     * When provided, VirtualList listens to this element's scroll events instead of the window.
     *
     * When omitted, VirtualList auto-detects the nearest ancestor with `overflow-y: auto|scroll`.
     * Falls back to window if no scrollable ancestor is found.
     * Use this prop as an override when auto-detection picks the wrong ancestor.
     */
    scrollContainer?: React.RefObject<HTMLElement | null>;

    /**
     * Called when the user scrolls near the load-more edge of the list:
     * bottom by default, top when `reversed` is true.
     * Uses IntersectionObserver on a sentinel element at the load-more edge.
     */
    onReachedEnd?: () => void;

    /**
     * Distance in pixels before the actual load-more edge to trigger `onReachedEnd`.
     * Maps to IntersectionObserver's rootMargin (bottom edge by default,
     * top edge when `reversed` is true).
     * Default: 200
     */
    reachedEndThreshold?: number;

    /**
     * Called when the user reaches the bottom edge of the scroll region.
     *
     * Fires on the transition from "scrolled away" → "at bottom" (NOT every
     * scroll tick while pinned at the bottom). "At bottom" means the live
     * `scrollHeight - scrollTop - clientHeight` distance is less than or
     * equal to `scrolledAwayFromBottomThreshold` px.
     *
     * Independent of `reversed`: a top-anchored list and a chat-style
     * reversed list both fire this when the user's viewport reaches the
     * bottom edge of the content. Pair with `onScrolledAwayFromBottom` to
     * track the at-bottom / away-from-bottom toggle.
     *
     * Detection is driven by the existing scroll listener (the same one
     * `updateVisibleRange` runs from), debounced inside a
     * `requestAnimationFrame` — no extra IntersectionObserver / sentinel.
     */
    onScrolledToBottom?: () => void;

    /**
     * Called when the user scrolls AWAY from the bottom edge.
     *
     * Fires on the transition from "at bottom" → "scrolled away". See
     * `onScrolledToBottom` for the "at bottom" definition. Like
     * `onScrolledToBottom`, fires only on transition — NOT every scroll
     * tick while the user is away from the bottom.
     */
    onScrolledAwayFromBottom?: () => void;

    /**
     * Px distance from the bottom edge at which the at-bottom / away-from-
     * bottom transition is detected. The `STICKY_BOTTOM_TOLERANCE_PX`
     * fudge factor used elsewhere in `reversed` mode is too small for an
     * intentional "scrolled up to read" signal — callers usually want a
     * generous-enough buffer that a user lingering near the bottom is
     * still counted as "at bottom".
     *
     * Default: `100`.
     */
    scrolledAwayFromBottomThreshold?: number;

    /**
     * When true, the list renders in chat-style mode: newest items live at
     * the bottom of the visible area and the load-more sentinel sits at the
     * top of the list.
     *
     * Semantics:
     * - On first non-empty mount the viewport anchors to the **bottom** of
     *   the list (latest item visible).
     * - When new items are **appended** (e.g. a freshly sent message) and
     *   the user was already pinned to the bottom, the viewport stays
     *   pinned — the list "sticks" to the bottom as it grows. If the user
     *   had scrolled up to read older content, the viewport is **not**
     *   yanked downward.
     * - When new items are **prepended** (e.g. an older page of messages
     *   loaded), the user's viewport offset relative to the existing content
     *   is preserved: the message that was at the top of the visible area
     *   before the prepend stays at the top after.
     * - When the rendered content's natural height is **smaller than the
     *   scroll viewport** (e.g. a freshly mounted list with only 2–3
     *   messages, or an empty list growing one message at a time), items
     *   pin to the **bottom** of the visible area — newest at the bottom,
     *   growing upward — mirroring `ListView(reverse: true)` in Flutter.
     *   This is achieved by giving the inner container a `paddingTop` equal
     *   to `clientHeight - naturalContentHeight`; the padding drops to 0
     *   as soon as content overflows the viewport and the normal
     *   `scrollTop`-driven anchor math takes over.
     * - `onReachedEnd` fires when the user scrolls near the **top** of the
     *   list (the load-more-older edge).
     *
     * Items are still fed in chronological order (oldest at index 0, newest
     * at the end) and still render top-to-bottom in the DOM — `reversed`
     * only changes scroll anchoring, not array/DOM order.
     *
     * List mode only — incompatible with `gridProps`, `initialIndex`, and
     * `direction === 'horizontal'` (a dev-mode warning is emitted and the
     * `reversed` flag is treated as `false` in those cases).
     *
     * **Tuning note:** the initial render window in reversed mode is sized
     * as `Math.ceil(viewportHeight * initialWindowSize / (defaultItemHeight + gap))`.
     * The default `defaultItemHeight` is 600px (tuned for full-bleed content
     * cards). For chat-style usage where each item is a small bubble
     * (~40–60px) callers SHOULD pass a realistic `defaultItemHeight` —
     * otherwise far fewer items than expected are rendered initially and the
     * top sentinel can sit very close to the visible area, firing
     * `onReachedEnd` immediately on mount.
     *
     * `defaultItemHeight` only sizes the initial render window — the
     * first-mount bottom-anchor itself does NOT depend on it being an
     * accurate average. After the two-RAF snap, the bottom-anchor effect
     * re-snaps in a settle loop until `scrollHeight` stops growing, so a
     * `defaultItemHeight` smaller than the measured row heights (e.g. 48px
     * hint for 150px-tall bubbles) still lands at the true bottom.
     *
     * **Scroll-region padding caveat:** callers SHOULD NOT add vertical
     * (`paddingTop` / `paddingBottom`) padding to the element passed via
     * `scrollContainer`. The bottom-pin math reads that element's
     * `clientHeight` (which includes its padding-box) and subtracts the
     * computed `paddingTop`/`paddingBottom` to recover the available
     * content area; if a caller stacks additional vertical padding on the
     * scroll region the math still works but couples this component to the
     * caller's computed style on every observer fire. For breathing room
     * above the first / below the last bubble, apply that padding INSIDE
     * `renderItem` (e.g. a `paddingTop` on the index-0 item) or use the
     * `gap` prop — horizontal padding on the scroll region is fine.
     *
     * Default: `false`.
     */
    reversed?: boolean;

    /**
     * Extra chrome (a sticky header and/or footer) that overlaps the *window*
     * viewport and must be excluded when the reversed engine decides how tall
     * the short-list bottom-pin padding has to be.
     *
     * Background: the reversed bottom-pin treats the scroll element's height
     * as the visible message strip. For a local `overflow-y: auto` div that is
     * exactly true. For a `window` scroll target, `window.innerHeight` is the
     * WHOLE viewport — including any sticky app-bar / composer zones rendered
     * in document flow on top of the list. Without this inset a short list
     * (2–3 messages) pads to fill the entire `innerHeight` instead of
     * `innerHeight − top − bottom`, producing blank scrollable space and a
     * newest bubble offset from a sticky footer.
     *
     * - `top`    — height of fixed/sticky chrome above the list (e.g. app bar
     *              [+ a no-internet banner]).
     * - `bottom` — height of fixed/sticky chrome below the list (e.g. the chat
     *              composer).
     *
     * **No-op unless** `reversed` is set AND the resolved scroll element is
     * `window`. For a local `HTMLElement` scroll container the existing
     * padding-box subtraction already accounts for the visible area, so this
     * inset is ignored there. Both fields default to `0`, so existing callers
     * that don't pass it see zero behavior change.
     *
     * Re-evaluated for free on viewport changes: the reversed engine's window
     * `resize` recompute path re-fires the bottom-pin computation, so a
     * changing inset (measured chrome that grows/shrinks) takes effect on the
     * next recompute.
     *
     * Default: `{}` (→ top 0, bottom 0).
     */
    reversedBottomPinInset?: { top?: number; bottom?: number };

    /**
     * Optional QA tag spread onto the list root container (`data-qa-id`), so a
     * feature list can be named for the `qa-tester` agent. Individual
     * virtualized rows are NOT tagged here — tag them in the row component the
     * feature passes via `renderItem`.
     */
    qa?: QaAttrOptions;
}

export const VirtualList = forwardRef(<T,>(
    {
        items,
        renderItem,
        getItemKey,
        initialWindowSize = 3,
        defaultItemHeight = 600,
        className = '',
        style,
        gap = 16,
        direction = 'vertical',
        isActive = true,
        enablePeriodicUpdates = false,
        onVisibleItemsChange,
        initialIndex,
        scrollOffsetTop = 0,
        backgroundColor,
        gridProps,
        scrollContainer,
        onReachedEnd,
        reachedEndThreshold = 200,
        onScrolledToBottom,
        onScrolledAwayFromBottom,
        scrolledAwayFromBottomThreshold = 100,
        reversed: reversedProp = false,
        reversedBottomPinInset,
        qa,
    }: VirtualListProps<T>,
    ref: React.Ref<VirtualListHandle>
) => {
    const {
        columnsCount = 1,
        crossAxisSpacing = 0,
        mainAxisSpacing = 0,
        itemAspectRatio = CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows = 5,
        padding = 0,
    } = gridProps ?? {};

    // Derived constant — true when operating as a virtualized grid rather than a list.
    // In grid mode: visibleRange/viewportRange hold ROW indices (not item indices).
    // External API (scrollToIndex, getViewportRange, onVisibleItemsChange) always uses item indices.
    if (process.env.NODE_ENV !== 'production' && gridProps !== undefined && columnsCount <= 1) {
        appLogger.warning('gridProps.columnsCount must be > 1 to enable grid mode. Falling back to list mode.', 'VirtualList');
    }
    const isGridMode = columnsCount > 1;

    // Resolve `reversed` mode: requested-but-incompatible combinations fall back
    // to top-anchored mode with a dev-mode warning. Reversed mode is list-only,
    // vertical-only, and incompatible with `initialIndex` (the bottom-anchor is
    // the de-facto initial position).
    if (process.env.NODE_ENV !== 'production' && reversedProp) {
        if (gridProps !== undefined) {
            appLogger.warning('`reversed` is incompatible with `gridProps`. Falling back to top-anchored mode.', 'VirtualList');
        }
        if (initialIndex !== undefined) {
            appLogger.warning('`reversed` is incompatible with `initialIndex`. Ignoring `initialIndex`.', 'VirtualList');
        }
        if (direction === 'horizontal') {
            appLogger.warning('`reversed` is incompatible with `direction="horizontal"`. Falling back to top-anchored mode.', 'VirtualList');
        }
    }
    const isReversed = reversedProp && !isGridMode && direction !== 'horizontal';
    // Effective initialIndex: ignored when reversed mode is active (bottom-anchor wins).
    const effectiveInitialIndex = isReversed ? undefined : initialIndex;

    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
    // Measured item heights, keyed by `toHeightCacheKey(getItemKey(...))` — a
    // STRING, because the measurement observer can only read the key back off
    // the DOM (`data-key`). See `toHeightCacheKey` for the full rationale.
    const itemHeights = useRef<Map<string, number>>(new Map());
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });
    const [viewportRange, setViewportRange] = useState({ start: 0, end: 0 });
    const measurementObserver = useRef<ResizeObserver | null>(null);
    const previousItemsLengthRef = useRef(0);
    const hasScrolledToInitialIndex = useRef(false);
    const initialScrollAttempts = useRef(0);
    const maxScrollAttempts = 10;

    // Latest `scrollOffsetTop`, reachable from the two landing paths WITHOUT
    // putting the prop in their dependency lists (same technique as
    // `latestUpdateVisibleRangeRef` below, refreshed on every render):
    // - the `initialIndex` effect schedules a retry loop over `setTimeout`, so a
    //   closure capture would freeze the value measured on the first commit —
    //   which is 0 for a caller that measures its app bar, because the measured
    //   height only lands on the NEXT render. Adding the prop to the deps
    //   instead would tear down and re-schedule that loop mid-retry.
    // - `scrollToIndex` is handed out through `useImperativeHandle`; reading the
    //   ref keeps the exposed handle identity stable.
    const scrollOffsetTopRef = useRef(scrollOffsetTop);
    scrollOffsetTopRef.current = scrollOffsetTop;
    // const initTimestamp = useRef<number>(Date.now());
    const reachedEndSentinelRef = useRef<HTMLDivElement>(null);

    // Resolve the scroll element: explicit scrollContainer prop, auto-detected scrollable ancestor, or window.
    const resolvedScrollContainerRef = useRef<HTMLElement | Window | null>(null);

    const getScrollElement = useCallback((): HTMLElement | Window => {
        // Explicit prop takes priority
        if (scrollContainer?.current) return scrollContainer.current;

        // Return cached auto-detected result
        if (resolvedScrollContainerRef.current) return resolvedScrollContainerRef.current;

        // Auto-detect: walk up from containerRef to find nearest scrollable ancestor
        if (containerRef.current) {
            let parent = containerRef.current.parentElement;
            while (parent) {
                const { overflowY } = getComputedStyle(parent);
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    resolvedScrollContainerRef.current = parent;
                    return parent;
                }
                parent = parent.parentElement;
            }
        }

        // Fallback to window
        resolvedScrollContainerRef.current = window;
        return window;
    }, [scrollContainer]);

    const getScrollTop = useCallback((el: HTMLElement | Window): number =>
        el instanceof Window ? (window.scrollY || document.documentElement.scrollTop) : el.scrollTop,
    []);

    const getViewportHeight = useCallback((el: HTMLElement | Window): number =>
        el instanceof Window ? window.innerHeight : el.clientHeight,
    []);

    // Grid mode: track container content-box width for row height calculation.
    // contentRect.width from ResizeObserver excludes CSS padding, so it's the inner usable width.
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const containerWidthObserver = useRef<ResizeObserver | null>(null);

    // Track if we're in measurement phase (measuring all items for accurate initialIndex scroll).
    // Grid mode never enters measurement phase — scroll position is computed arithmetically.
    const [isMeasuringForInitialIndex, setIsMeasuringForInitialIndex] = useState(
        !isGridMode && effectiveInitialIndex !== undefined && effectiveInitialIndex >= 0
    );

    // Mutable handle to the latest `updateVisibleRange` callback. The scroll
    // listener invokes it via `latestUpdateVisibleRangeRef.current?.()` so the
    // scroll listener does NOT need `updateVisibleRange` in its deps — which
    // would otherwise force it to re-subscribe (and clobber the reversed-mode
    // pre-update scroll-metrics snapshot owned by `useReversedAnchor`) on
    // every items change. Refreshed on every render so callers always get
    // the freshest closure over `items`. See the CRITICAL block on the
    // scroll-listener effect below for the matching half of this invariant.
    const latestUpdateVisibleRangeRef = useRef<(() => void) | null>(null);

    // Reversed-mode subsystem: first-mount bottom-anchor + settle loop,
    // sticky-on-append / preserve-on-prepend, bottom-pin padding for short
    // lists, and the gate for the top-sentinel IntersectionObserver. See
    // `useReversedAnchor.ts` for the full implementation and rationale.
    // On `isReversed=false` this is a near-no-op (returns zeroed values and
    // skips subscribing observers).
    const {
        bottomPinPaddingTop,
        hasInitialBottomAnchorCompleted,
        refreshLatestScrollMetricsRef,
    } = useReversedAnchor({
        isReversed,
        items,
        getItemKey,
        containerRef,
        getScrollElement,
        getScrollTop,
        getViewportHeight,
        updateVisibleRangeRef: latestUpdateVisibleRangeRef,
        bottomPinInsetTop: reversedBottomPinInset?.top ?? 0,
        bottomPinInsetBottom: reversedBottomPinInset?.bottom ?? 0,
    });

    // Helper to get item height (measured or default).
    // The key is normalised so a numeric `getItemKey` result still matches the
    // string key the measurement observer wrote (see `toHeightCacheKey`).
    const getItemHeight = useCallback((key: string | number): number => {
        return itemHeights.current.get(toHeightCacheKey(key)) ?? defaultItemHeight;
    }, [defaultItemHeight]);

    // Grid mode: fixed row height derived from container width and item aspect ratio.
    // contentRect.width (from ResizeObserver) is the content-box width — CSS padding is already excluded.
    const gridRowHeight = useMemo(() => {
        if (!isGridMode || containerWidth === 0) return 0;
        const availableWidth = containerWidth - (columnsCount - 1) * crossAxisSpacing;
        return (availableWidth / columnsCount) / itemAspectRatio;
    }, [isGridMode, containerWidth, columnsCount, crossAxisSpacing, itemAspectRatio]);

    // Number of rows in grid mode; equals items.length in list mode (unused in list mode).
    const rowCount = useMemo(() =>
        isGridMode ? Math.ceil(items.length / columnsCount) : items.length,
        [isGridMode, items.length, columnsCount]);

    // Grid mode: observe container width so row heights stay correct on resize.
    useEffect(() => {
        if (!isGridMode) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const newWidth = entry.contentRect.width;
            // Ignore zero-width reports — this happens when the container's ancestor becomes
            // display:none (e.g. a cached route hidden by RouteStack). Keeping the last valid
            // width prevents gridRowHeight from collapsing to 0, which preserves page height
            // and allows scroll restoration to succeed when the route becomes visible again.
            if (newWidth === 0) return;
            setContainerWidth(prev => prev === newWidth ? prev : newWidth);
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        containerWidthObserver.current = observer;

        return () => {
            observer.disconnect();
            containerWidthObserver.current = null;
        };
    }, [isGridMode]);

    // Get position of an item by querying its actual position in the DOM.
    // In grid mode: computed arithmetically from row index (no DOM query needed).
    const getItemPosition = useCallback((index: number): number | null => {
        if (index < 0 || index >= items.length || !containerRef.current) return null;

        if (isGridMode) {
            if (gridRowHeight === 0) return null;
            const rowIndex = Math.floor(index / columnsCount);
            return rowIndex * (gridRowHeight + mainAxisSpacing);
        }

        // List mode: query DOM for the element
        const element = containerRef.current.querySelector(
            `[data-index="${index}"]`
        ) as HTMLElement | null;

        if (!element) {
            appLogger.warning(`Element not found for index ${index}`, 'VirtualList.getItemPosition');
            return null;
        }

        return element.offsetTop;
    }, [items.length, isGridMode, gridRowHeight, columnsCount, mainAxisSpacing]);

    // Scroll to a specific item index.
    // Grid mode: computed arithmetically from row index (no DOM query needed).
    // List mode: uses DOM offsetTop.
    const scrollToIndex = useCallback((index: number, behavior: ScrollBehavior = 'smooth') => {
        if (!containerRef.current || index < 0 || index >= items.length) return;

        const scrollEl = getScrollElement();
        const isLocalScroll = !(scrollEl instanceof Window);

        // Breathing room + the caller's sticky top chrome (`scrollOffsetTop`),
        // so the target lands below the chrome rather than behind it.
        const topSpacing = LANDING_TOP_SPACING + scrollOffsetTopRef.current;

        if (isGridMode) {
            if (gridRowHeight === 0) return;
            const rowIndex = Math.floor(index / columnsCount);
            const rowOffsetTop = rowIndex * (gridRowHeight + mainAxisSpacing);
            if (isLocalScroll) {
                const scrollParent = scrollEl as HTMLElement;
                const relativeTop = containerRef.current.offsetTop;
                scrollParent.scrollTo({ top: rowOffsetTop + relativeTop - topSpacing, behavior });
            } else {
                const containerRect = containerRef.current.getBoundingClientRect();
                const containerTop = containerRect.top + window.scrollY;
                window.scrollTo({ top: rowOffsetTop + containerTop - topSpacing, behavior });
            }
            return;
        }

        // List mode: query DOM for the target element
        const targetElement = containerRef.current.querySelector(
            `[data-index="${index}"]`
        ) as HTMLElement | null;

        if (!targetElement) {
            appLogger.warning(`Cannot scroll: element not found for index ${index}`, 'VirtualList.scrollToIndex');
            return;
        }

        // Get the element's position relative to the container
        const elementOffsetTop = targetElement.offsetTop;

        if (isLocalScroll) {
            const scrollParent = scrollEl as HTMLElement;
            const relativeTop = containerRef.current.offsetTop;
            const targetScrollPos = elementOffsetTop + relativeTop - topSpacing;
            scrollParent.scrollTo({ top: targetScrollPos, behavior });
        } else {
            // Get container's position relative to viewport
            const containerRect = containerRef.current.getBoundingClientRect();
            const containerTop = containerRect.top + window.scrollY;
            const targetScrollY = elementOffsetTop + containerTop - topSpacing;
            window.scrollTo({ top: targetScrollY, behavior });
        }
    }, [items.length, isGridMode, gridRowHeight, columnsCount, mainAxisSpacing, getScrollElement]);

    // Imperative "scroll to bottom" — kept as a sibling of `scrollToIndex` so
    // both imperative scroll primitives share the same scroll-element
    // resolution (`getScrollElement()`) and callers never need to know whether
    // the scroll target is the `scrollContainer` prop, the nearest scrollable
    // ancestor, or `window`.
    //
    // This deliberately does NOT route through `useReversedAnchor` — that hook
    // owns the bottom-pin / mount-anchor lifecycle, not arbitrary imperative
    // scrolls. The caller's intent here ("bring the new message into view")
    // must override any "leave alone" decision the anchor effect would make
    // (e.g. user scrolled up and then sent a message).
    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        const scrollEl = getScrollElement();
        const isWindow = scrollEl instanceof Window;

        // Read the current max scrollTop from the live DOM. Captured fresh on
        // each pass so the settle loop can converge as late-arriving item
        // measurements grow the container's `scrollHeight`.
        const readMaxScrollTop = (): number => {
            if (isWindow) return document.documentElement.scrollHeight - window.innerHeight;
            const el = scrollEl as HTMLElement;
            return el.scrollHeight - el.clientHeight;
        };
        // `Math.max(0, …)` guards the no-overflow case (e.g. bottom-pin
        // padding has expanded the container to exactly `clientHeight`,
        // making the diff zero or — under sub-pixel drift — negative).
        const applyScrollTop = (top: number): void => {
            const safe = Math.max(0, top);
            if (isWindow) window.scrollTo({ top: safe, behavior });
            else (scrollEl as HTMLElement).scrollTo({ top: safe, behavior });
        };
        const readScrollHeight = (): number => {
            if (isWindow) return document.documentElement.scrollHeight;
            return (scrollEl as HTMLElement).scrollHeight;
        };

        // Initial synchronous snap from whatever the DOM looks like right now.
        // Preserves the immediate behavior callers expect (and lets a
        // `'smooth'` behavior begin animating without a frame delay).
        let lastTarget = readMaxScrollTop();
        applyScrollTop(lastTarget);

        // Settle loop: re-snap while `scrollHeight` is still growing under
        // late-arriving item measurements (e.g. a freshly-appended message
        // whose real height is larger than `defaultItemHeight`). Mirrors the
        // mount-time settle loop in `useReversedAnchor` — same
        // `MAX_SETTLE_PASSES = 5` and same exit condition (scrollHeight
        // stable from the previous pass).
        const MAX_SETTLE_PASSES = 5;
        let pass = 0;
        let prevScrollHeight = readScrollHeight();
        const settle = () => {
            const newTarget = readMaxScrollTop();
            if (newTarget !== lastTarget) {
                lastTarget = newTarget;
                applyScrollTop(lastTarget);
            }
            const newScrollHeight = readScrollHeight();
            const stable = newScrollHeight === prevScrollHeight;
            prevScrollHeight = newScrollHeight;
            pass += 1;
            if (!stable && pass < MAX_SETTLE_PASSES) requestAnimationFrame(settle);
        };
        requestAnimationFrame(settle);
    }, [getScrollElement]);

    // Expose imperative methods via ref.
    // getViewportRange/getRenderedRange always return item-level indices.
    // In grid mode, visibleRange/viewportRange hold row indices internally — convert here.
    useImperativeHandle(ref, () => ({
        scrollToIndex,
        scrollToBottom,
        getItemPosition,
        getViewportRange: () => isGridMode
            ? {
                start: viewportRange.start * columnsCount,
                end: Math.min(items.length - 1, viewportRange.end * columnsCount - 1),
            }
            : { ...viewportRange },
        getRenderedRange: () => isGridMode
            ? {
                start: visibleRange.start * columnsCount,
                end: Math.min(items.length - 1, visibleRange.end * columnsCount - 1),
            }
            : { ...visibleRange },
    }), [scrollToIndex, scrollToBottom, getItemPosition, viewportRange, visibleRange, isGridMode, columnsCount, items.length]);

    // Calculate which items/rows should be visible based on scroll position.
    // Grid mode: uses row arithmetic (visibleRange/viewportRange store ROW indices).
    // List mode: uses measured/default item heights (visibleRange/viewportRange store ITEM indices).
    const updateVisibleRange = useCallback(() => {
        // Skip updates when inactive (route not visible) or during measurement phase
        if (!isActive || !containerRef.current || items.length === 0 || isMeasuringForInitialIndex) return;

        const scrollEl = getScrollElement();
        const scrollTop = getScrollTop(scrollEl);
        const viewportHeight = getViewportHeight(scrollEl);
        const renderWindow = viewportHeight * initialWindowSize;
        const containerTop = scrollEl instanceof Window
            ? containerRef.current.getBoundingClientRect().top + scrollTop
            : containerRef.current.offsetTop;

        // Calculate scroll position relative to container
        const scrollRelativeToContainer = Math.max(0, scrollTop - containerTop);
        const scrollBottom = scrollRelativeToContainer + viewportHeight;

        // ---- GRID MODE: arithmetic row calculation ----
        if (isGridMode) {
            if (gridRowHeight === 0 || rowCount === 0) return;
            const rowStep = gridRowHeight + mainAxisSpacing;

            const viewportStartRow = Math.max(0, Math.floor(scrollRelativeToContainer / rowStep));
            const viewportEndRow = Math.min(rowCount, Math.ceil(scrollBottom / rowStep));
            const renderStartRow = Math.max(0, viewportStartRow - bufferRows);
            const renderEndRow = Math.min(rowCount, viewportEndRow + bufferRows);

            setVisibleRange(prev =>
                prev.start !== renderStartRow || prev.end !== renderEndRow
                    ? { start: renderStartRow, end: renderEndRow }
                    : prev
            );
            setViewportRange(prev =>
                prev.start !== viewportStartRow || prev.end !== viewportEndRow
                    ? { start: viewportStartRow, end: viewportEndRow }
                    : prev
            );
            return;
        }

        // ---- LIST MODE: loop over measured/default item heights ----
        let currentTop = 0;
        let renderStartIndex = 0;
        let renderEndIndex = items.length;
        let viewportStartIndex = 0;
        let viewportEndIndex = items.length;
        let foundRenderStart = false;
        let foundViewportStart = false;
        let foundViewportEnd = false;

        for (let i = 0; i < items.length; i++) {
            const key = getItemKey(items[i], i);
            const itemHeight = getItemHeight(key);
            // Each item takes its height + gap after it (matches marginBottom in DOM)
            const itemBottom = currentTop + itemHeight + gap;

            // Track items actually in viewport (no buffer)
            if (!foundViewportStart && itemBottom >= scrollRelativeToContainer) {
                viewportStartIndex = i;
                foundViewportStart = true;
            }
            if (foundViewportStart && !foundViewportEnd && currentTop > scrollBottom) {
                viewportEndIndex = i;
                foundViewportEnd = true;
            }

            // Start rendering items within the render window before viewport (with buffer)
            if (!foundRenderStart && itemBottom >= scrollRelativeToContainer - renderWindow) {
                renderStartIndex = i;
                foundRenderStart = true;
            }

            // Stop rendering after render window past viewport (with buffer)
            if (foundRenderStart && currentTop > scrollBottom + renderWindow) {
                renderEndIndex = i;
                break;
            }

            currentTop = itemBottom;
        }

        setVisibleRange(prev => {
            if (prev.start !== renderStartIndex || prev.end !== renderEndIndex) {
                return { start: renderStartIndex, end: renderEndIndex };
            }
            return prev;
        });

        setViewportRange(prev => {
            if (prev.start !== viewportStartIndex || prev.end !== viewportEndIndex) {
                return { start: viewportStartIndex, end: viewportEndIndex };
            }
            return prev;
        });
    }, [
        items, getItemKey, gap, initialWindowSize, defaultItemHeight, isActive, isMeasuringForInitialIndex,
        isGridMode, gridRowHeight, rowCount, mainAxisSpacing, bufferRows,
        getScrollElement, getScrollTop, getViewportHeight,
    ]);

    // Keep the latest `updateVisibleRange` reachable from effects that must
    // NOT re-subscribe when `items` changes (e.g. the scroll listener below).
    // See the comment on `latestUpdateVisibleRangeRef`'s declaration above.
    latestUpdateVisibleRangeRef.current = updateVisibleRange;

    // Measure item heights using ResizeObserver (list mode only — grid mode uses fixed computed heights)
    useEffect(() => {
        if (isGridMode) return;

        measurementObserver.current = new ResizeObserver((entries) => {
            let hasChanges = false;

            entries.forEach((entry) => {
                const element = entry.target as HTMLDivElement;
                // Always a string (or null when the attribute is absent) — this
                // is the reason the cache is keyed by string; `getItemHeight`
                // normalises the read side to match. A `'0'` key is valid, so
                // reject only the absent case, never falsy strings.
                const key = element.getAttribute('data-key');
                if (key === null) return;

                const height = entry.contentRect.height;
                const previousHeight = itemHeights.current.get(key);

                if (previousHeight !== height) {
                    itemHeights.current.set(key, height);
                    hasChanges = true;
                }
            });

            // Recalculate visible range if any heights changed
            if (hasChanges) {
                requestAnimationFrame(updateVisibleRange);
            }
        });

        return () => {
            measurementObserver.current?.disconnect();
        };
    }, [updateVisibleRange, isGridMode]);

    // Setup scroll listener.
    //
    // In reversed mode this listener is ALSO responsible for keeping the
    // pre-update scroll-metrics snapshot (owned by `useReversedAnchor` and
    // exposed via `refreshLatestScrollMetricsRef`) continuously fresh, so the
    // hook's items-change effect can read pre-update metrics without racing
    // the React commit.
    //
    // CRITICAL: this effect's dep array DELIBERATELY omits
    // `updateVisibleRange` AND `refreshLatestScrollMetricsRef.current` (we
    // depend only on the ref's identity, which is stable). `updateVisibleRange`
    // depends on `items`, so if it were in the deps here the effect would
    // tear down + re-subscribe on every items change. The re-subscribe body
    // would re-seed the metrics snapshot AFTER React's commit phase had
    // already pushed the new items into the DOM — overwriting the user's
    // pre-update snapshot with post-commit metrics and breaking both
    // sticky-on-append and preserve-on-prepend. To call the freshest
    // `updateVisibleRange` and the freshest refresher we route through their
    // refs (`latestUpdateVisibleRangeRef` / `refreshLatestScrollMetricsRef`)
    // which are reassigned each render but whose identities are stable. The
    // seed at subscription time therefore only runs once on genuine mount (or
    // when `isActive` / `getScrollElement` toggle), NOT on items change.
    useEffect(() => {
        // Don't set up scroll listener if inactive
        if (!isActive) return;

        const scrollEl = getScrollElement();

        // Seed once at subscription time so the FIRST items-change effect
        // (which may run before any scroll event) has a snapshot to read.
        // Because this effect does NOT depend on `items`, this seed only
        // runs on genuine subscribe (mount or one of the reactive deps
        // below changing) — NOT on items change.
        refreshLatestScrollMetricsRef.current?.();

        let rafId: number;

        const handleScroll = () => {
            // Refresh the reversed-mode metrics snapshot synchronously on
            // every scroll event — cheap, and ensures the ref reflects the
            // user's last-observed position when the next items-change
            // effect fires.
            refreshLatestScrollMetricsRef.current?.();
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                latestUpdateVisibleRangeRef.current?.();
            });
        };

        scrollEl.addEventListener('scroll', handleScroll, { passive: true });
        // Initial calculation
        latestUpdateVisibleRangeRef.current?.();

        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
        // NOTE: `updateVisibleRange` is intentionally NOT in deps — see the
        // CRITICAL block in the comment above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, getScrollElement]);

    // Bottom-edge transition detection — drives `onScrolledToBottom` /
    // `onScrolledAwayFromBottom`. Fires only on the at-bottom <-> away-from-
    // bottom transition (NOT every scroll tick while pinned at either edge).
    //
    // Implementation note: subscribes a dedicated scroll listener on the same
    // resolved scroll element rather than piggy-backing on the main listener
    // above. The main listener's effect deliberately has a minimal dep array
    // (`[isActive, getScrollElement]`) to preserve the "no re-subscribe on
    // items change" invariant; folding the bottom-edge callbacks into it
    // would force them into the dep array and risk re-subscription churn.
    // Keeping a separate effect with its own deps is cheaper than that risk
    // and lets each callback be swapped independently.
    //
    // Detection uses `scrollHeight - scrollTop - clientHeight <= threshold`
    // as the "at bottom" predicate — works identically for top-anchored and
    // reversed lists (both share the same DOM scroll metrics). Debounced
    // inside a `requestAnimationFrame` so a burst of scroll events produces
    // at most one transition check per frame.
    useEffect(() => {
        if (!isActive) return;
        if (!onScrolledToBottom && !onScrolledAwayFromBottom) return;

        const scrollEl = getScrollElement();
        let rafId = 0;
        // Initial state is unknown — we let the first frame establish it
        // without firing either callback, so callers see transitions only
        // (matches the contract). Subsequent frames compare against this
        // tracked value and fire on change.
        let lastAtBottom: boolean | null = null;

        const readDistanceFromBottom = (): number => {
            if (scrollEl instanceof Window) {
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                return document.documentElement.scrollHeight - scrollTop - window.innerHeight;
            }
            const el = scrollEl as HTMLElement;
            return el.scrollHeight - el.scrollTop - el.clientHeight;
        };

        const checkTransition = () => {
            const distance = readDistanceFromBottom();
            const atBottom = distance <= scrolledAwayFromBottomThreshold;
            if (lastAtBottom === null) {
                lastAtBottom = atBottom;
                // Seed the contract: fire `onScrolledToBottom` once on mount
                // if the initial state IS at-bottom. The chat use-case relies
                // on this — a freshly-anchored list at the bottom must mark
                // the newest visible message as seen even when the user has
                // not scrolled yet. (A reversed list's mount-time anchor lands
                // here; a top-anchored list typically does not.)
                if (atBottom) onScrolledToBottom?.();
                return;
            }
            if (atBottom === lastAtBottom) return;
            lastAtBottom = atBottom;
            if (atBottom) onScrolledToBottom?.();
            else onScrolledAwayFromBottom?.();
        };

        const handleScroll = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(checkTransition);
        };

        scrollEl.addEventListener('scroll', handleScroll, { passive: true });
        // First check after one frame so the layout has settled (the mount-
        // time bottom-anchor snap in `useReversedAnchor` runs across two
        // RAFs — checking synchronously here would read pre-anchor metrics
        // and mis-fire `onScrolledAwayFromBottom` before flipping back).
        rafId = requestAnimationFrame(checkTransition);

        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [
        isActive,
        getScrollElement,
        onScrolledToBottom,
        onScrolledAwayFromBottom,
        scrolledAwayFromBottomThreshold,
    ]);

    // Initialize visible range on mount and when items change
    useEffect(() => {
        if (items.length === 0) {
            setVisibleRange({ start: 0, end: 0 });
            previousItemsLengthRef.current = 0;
            return;
        }

        const previousLength = previousItemsLengthRef.current;
        const currentLength = items.length;
        previousItemsLengthRef.current = currentLength;

        // Only reset to top on initial mount (0 -> n items)
        // For pagination (items appended), preserve current position
        const isInitialLoad = previousLength === 0 && currentLength > 0;

        if (isInitialLoad) {
            // Reversed mode: render the LAST `estimatedItemsInWindow` items and
            // anchor the scroll to the bottom after first paint (handled below
            // in the bottom-anchor effect). Bypasses the top-anchored branches
            // entirely — `effectiveInitialIndex` is already `undefined` in this
            // mode (the bottom-anchor IS the initial position).
            if (isReversed) {
                const scrollEl = getScrollElement();
                const viewportHeight = getViewportHeight(scrollEl);
                const renderWindow = viewportHeight * initialWindowSize;
                const estimatedItemsInWindow = Math.ceil(renderWindow / (defaultItemHeight + gap));
                const start = Math.max(0, currentLength - estimatedItemsInWindow);
                setVisibleRange({ start, end: currentLength });
            } else if (effectiveInitialIndex !== undefined && effectiveInitialIndex >= 0 && effectiveInitialIndex < currentLength) {
                // If initialIndex is provided, determine initial render range
                if (isGridMode) {
                    // Grid mode: no measurement phase needed — row heights are computed.
                    // Set initial visible range (in row indices) centered around the target row.
                    const targetRow = Math.floor(effectiveInitialIndex / columnsCount);
                    const scrollEl = getScrollElement();
                    const viewportHeight = getViewportHeight(scrollEl);
                    const rowStep = (gridRowHeight || defaultItemHeight) + mainAxisSpacing;
                    const rowsInWindow = Math.ceil((viewportHeight * initialWindowSize) / rowStep);
                    const renderStart = Math.max(0, targetRow - Math.floor(rowsInWindow / 2));
                    const totalRows = Math.ceil(currentLength / columnsCount);
                    const renderEnd = Math.min(totalRows, renderStart + rowsInWindow);
                    setVisibleRange({ start: renderStart, end: renderEnd });
                } else {
                    // List mode: render items 0..initialIndex + buffer off-screen for height measurement
                    const bufferAfter = Math.min(5, currentLength - effectiveInitialIndex - 1);
                    const endIndex = Math.min(effectiveInitialIndex + bufferAfter + 1, currentLength);
                    setVisibleRange({ start: 0, end: endIndex });
                    setIsMeasuringForInitialIndex(true);
                }
            } else {
                // Calculate initial range based on viewport and default item height
                const scrollEl = getScrollElement();
                const viewportHeight = getViewportHeight(scrollEl);
                const renderWindow = viewportHeight * initialWindowSize;
                const estimatedItemsInWindow = Math.ceil(renderWindow / (defaultItemHeight + gap));
                const initialEnd = Math.min(estimatedItemsInWindow, currentLength);

                setVisibleRange({ start: 0, end: initialEnd });
            }
        }

        // Clear heights for items that no longer exist.
        // Normalised to the cache's key type — otherwise a numeric `getItemKey`
        // produces a Set of numbers that matches none of the string cache keys
        // and this pass wipes EVERY measured height on each items change.
        const currentKeys = new Set(items.map((item, i) => toHeightCacheKey(getItemKey(item, i))));
        itemHeights.current.forEach((_, key) => {
            if (!currentKeys.has(key)) {
                itemHeights.current.delete(key);
            }
        });

        // Recalculate after items render and get measured
        requestAnimationFrame(updateVisibleRange);
    }, [items, getItemKey, updateVisibleRange, initialWindowSize, defaultItemHeight, gap, effectiveInitialIndex,
        isGridMode, isReversed, columnsCount, gridRowHeight, mainAxisSpacing, getScrollElement, getViewportHeight]);

    // Scroll to initialIndex after initial render.
    // Grid mode: computed arithmetically — retries only if container width not yet measured.
    // List mode: queries DOM with retries until element appears after measurement phase.
    // Reversed mode: skipped entirely (effectiveInitialIndex is undefined when reversed).
    useEffect(() => {
        if (
            effectiveInitialIndex === undefined ||
            effectiveInitialIndex < 0 ||
            effectiveInitialIndex >= items.length ||
            hasScrolledToInitialIndex.current ||
            !containerRef.current
        ) {
            return;
        }

        const scrollToPosition = (targetOffsetTop: number) => {
            const scrollTarget = getScrollElement();
            // Read at call time (not at effect setup): this runs from a retry
            // loop, and a caller measuring its app bar only has the height on a
            // later render. Same math as `scrollToIndex` above.
            const topSpacing = LANDING_TOP_SPACING + scrollOffsetTopRef.current;
            if (scrollTarget instanceof Window) {
                const containerRect = containerRef.current!.getBoundingClientRect();
                const containerTop = containerRect.top + window.scrollY;
                window.scrollTo({ top: targetOffsetTop + containerTop - topSpacing, behavior: 'auto' });
            } else {
                const relativeTop = containerRef.current!.offsetTop;
                scrollTarget.scrollTo({ top: targetOffsetTop + relativeTop - topSpacing, behavior: 'auto' });
            }
        };

        const attemptScroll = () => {
            initialScrollAttempts.current++;

            // Grid mode: compute scroll position from row arithmetic (no DOM query needed)
            if (isGridMode) {
                if (gridRowHeight === 0) {
                    // Container width not measured yet — retry
                    if (initialScrollAttempts.current < maxScrollAttempts) {
                        setTimeout(attemptScroll, 50);
                    }
                    return;
                }
                const rowIndex = Math.floor(effectiveInitialIndex / columnsCount);
                const rowOffsetTop = rowIndex * (gridRowHeight + mainAxisSpacing);
                scrollToPosition(rowOffsetTop);
                hasScrolledToInitialIndex.current = true;
                return;
            }

            // List mode: query DOM for the target element using data-index attribute
            const targetElement = containerRef.current?.querySelector(
                `[data-index="${effectiveInitialIndex}"]`
            ) as HTMLElement | null;

            if (targetElement) {
                scrollToPosition(targetElement.offsetTop);
                hasScrolledToInitialIndex.current = true;

                // Exit measurement mode and resume normal virtualization
                setIsMeasuringForInitialIndex(false);
            } else if (initialScrollAttempts.current < maxScrollAttempts) {
                // Element not in DOM yet, try again
                setTimeout(attemptScroll, 50);
            } else {
                // Max attempts reached, give up and resume normal mode
                appLogger.warning('Failed to find element for scroll after max attempts', 'VirtualList.scrollToIndex');
                setIsMeasuringForInitialIndex(false);
            }
        };

        // Start attempting to scroll after a brief initial delay to allow render
        const timeoutId = setTimeout(attemptScroll, 100);

        return () => clearTimeout(timeoutId);
    }, [effectiveInitialIndex, items, isGridMode, gridRowHeight, columnsCount, mainAxisSpacing, getScrollElement]);

    // Notify when visible or rendered items change (not on every height measurement)
    const previousRangesRef = useRef({
        visibleStart: 0,
        visibleEnd: 0,
        viewportStart: 0,
        viewportEnd: 0
    });

    // Helper: convert internal range (which may be row indices in grid mode) to item-level indices.
    const toItemRange = useCallback((range: { start: number; end: number }) => {
        if (!isGridMode) return range;
        return {
            start: range.start * columnsCount,
            end: Math.min(items.length, range.end * columnsCount),
        };
    }, [isGridMode, columnsCount, items.length]);

    // Periodic safety check: emit updates every second even if ranges haven't changed
    // This ensures listeners don't miss updates due to edge cases
    useEffect(() => {
        if (!enablePeriodicUpdates || !onVisibleItemsChange || !isActive) return;

        const intervalId = setInterval(() => {
            if (visibleRange.end > visibleRange.start || viewportRange.end > viewportRange.start) {
                const vpItems = toItemRange(viewportRange);
                const rnItems = toItemRange(visibleRange);
                onVisibleItemsChange(
                    items.slice(vpItems.start, vpItems.end),
                    vpItems.start,
                    vpItems.end - 1,
                    items.slice(rnItems.start, rnItems.end),
                    rnItems.start,
                    rnItems.end - 1
                );
            }
        }, 1000); // Check every second

        return () => clearInterval(intervalId);
    }, [enablePeriodicUpdates, visibleRange, viewportRange, items, onVisibleItemsChange, isActive, toItemRange]);

    // Notify when visible or rendered items change (immediate updates on range changes)
    useEffect(() => {
        if (!onVisibleItemsChange) return;

        const prev = previousRangesRef.current;
        const rangeChanged =
            prev.visibleStart !== visibleRange.start ||
            prev.visibleEnd !== visibleRange.end ||
            prev.viewportStart !== viewportRange.start ||
            prev.viewportEnd !== viewportRange.end;

        if (rangeChanged && (visibleRange.end > visibleRange.start || viewportRange.end > viewportRange.start)) {
            previousRangesRef.current = {
                visibleStart: visibleRange.start,
                visibleEnd: visibleRange.end,
                viewportStart: viewportRange.start,
                viewportEnd: viewportRange.end,
            };

            const vpItems = toItemRange(viewportRange);
            const rnItems = toItemRange(visibleRange);
            onVisibleItemsChange(
                items.slice(vpItems.start, vpItems.end),
                vpItems.start,
                vpItems.end - 1,
                items.slice(rnItems.start, rnItems.end),
                rnItems.start,
                rnItems.end - 1
            );
        }
    }, [visibleRange, viewportRange, items, onVisibleItemsChange, toItemRange]);

    // Calculate spacer heights from measured/default item heights (list mode) or row arithmetic (grid mode).
    const { topSpacerHeight, bottomSpacerHeight } = useMemo(() => {
        // Grid mode: pure arithmetic using fixed row height
        if (isGridMode) {
            if (gridRowHeight === 0) return { topSpacerHeight: 0, bottomSpacerHeight: 0 };
            const rowStep = gridRowHeight + mainAxisSpacing;
            return {
                topSpacerHeight: visibleRange.start * rowStep,
                bottomSpacerHeight: Math.max(0, rowCount - visibleRange.end) * rowStep,
            };
        }

        // List mode: sum of measured/default heights for off-screen items
        let topHeight = 0;
        let bottomHeight = 0;

        for (let i = 0; i < visibleRange.start; i++) {
            const key = getItemKey(items[i], i);
            topHeight += getItemHeight(key) + gap;
        }

        for (let i = visibleRange.end; i < items.length; i++) {
            const key = getItemKey(items[i], i);
            bottomHeight += getItemHeight(key) + gap;
        }

        return { topSpacerHeight: topHeight, bottomSpacerHeight: bottomHeight };
    }, [isGridMode, gridRowHeight, mainAxisSpacing, rowCount, visibleRange, items, getItemKey, gap, getItemHeight]);

    // Register/unregister items with ResizeObserver (list mode only)
    const setItemRef = useCallback((key: string | number, element: HTMLDivElement | null) => {
        if (isGridMode) return; // Grid mode uses fixed computed heights — no measurement needed

        if (element) {
            itemRefs.current.set(key, element);
            measurementObserver.current?.observe(element);
        } else {
            const oldElement = itemRefs.current.get(key);
            if (oldElement) {
                measurementObserver.current?.unobserve(oldElement);
                itemRefs.current.delete(key);
            }
        }
    }, [isGridMode]);

    // onReachedEnd: observe a sentinel element at the load-more edge of the list.
    // Default mode: sentinel is at the bottom; rootMargin extends the bottom edge.
    // Reversed mode: sentinel is at the top; rootMargin extends the top edge.
    // Re-creates the observer when items.length changes so that if new items are added
    // but the sentinel is still visible (content fits in viewport), it fires again.
    //
    // Reversed-mode gate: skip creating the observer until the first-mount
    // bottom-anchor snap completes. At observer-creation time during the
    // two-RAF mount window, `scrollTop` is still 0 and the top sentinel sits
    // within the `rootMargin` extension → it would intersect and fire
    // `onReachedEnd` immediately, before the user has scrolled. Once the snap
    // lands, `hasInitialBottomAnchorCompleted` flips to `true`, this effect
    // re-runs, and the observer is created against a viewport that is
    // genuinely at the bottom.
    useEffect(() => {
        if (!onReachedEnd || !reachedEndSentinelRef.current) return;
        if (isReversed && !hasInitialBottomAnchorCompleted) return;

        const scrollEl = getScrollElement();
        const root = scrollEl instanceof Window ? null : scrollEl as HTMLElement;

        const rootMargin = isReversed
            ? `${reachedEndThreshold}px 0px 0px 0px`
            : `0px 0px ${reachedEndThreshold}px 0px`;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                const el = scrollEl instanceof Window
                    ? document.documentElement
                    : (scrollEl as HTMLElement);
                const hasOverflow = el.scrollHeight > el.clientHeight + STICKY_BOTTOM_TOLERANCE_PX;
                if (isReversed) {
                    // Only treat the top sentinel as a load-more trigger when the
                    // scroll region actually has content above the current view to
                    // reveal. A short list (content < viewport) has the sentinel
                    // permanently visible inside the rootMargin extension; firing
                    // there would call onReachedEnd with no user intent.
                    if (!hasOverflow) return;
                    // No additional `scrollTop` guard here: the `reachedEndThreshold`px
                    // rootMargin extension already defines "near the top", and the
                    // observer fires its single not-intersecting→intersecting transition
                    // exactly at that boundary. Re-checking `scrollTop > reachedEndThreshold`
                    // raced against that same boundary (the transition was almost always
                    // sampled a few px above the threshold and rejected, with no second
                    // callback until the user bounced down and back up). Letting the
                    // intersection state itself be the trigger removes that race.
                }
                onReachedEnd();
            },
            { root, rootMargin, threshold: 0 },
        );

        observer.observe(reachedEndSentinelRef.current);
        return () => observer.disconnect();
    }, [onReachedEnd, reachedEndThreshold, getScrollElement, items.length, isReversed, hasInitialBottomAnchorCompleted]);

    // Grid mode: item-level slice bounds derived from row-level visibleRange
    const renderStartItem = isGridMode ? visibleRange.start * columnsCount : visibleRange.start;
    const renderEndItem = isGridMode ? Math.min(items.length, visibleRange.end * columnsCount) : visibleRange.end;

    return (
        <div
            ref={containerRef}
            {...(qa ? qaAttr(qa) : {})}
            className={className}
            style={{
                ...(isGridMode ? {
                    backgroundColor,
                    padding: padding > 0 ? `${padding}px` : undefined,
                } : {}),
                ...style,
                // Reversed mode: pin items to the BOTTOM of the scroll viewport
                // when total natural content height is shorter than the viewport.
                // Padding goes to 0 once content overflows the viewport so the
                // normal scroll-anchor math takes over without interference.
                // Applied AFTER `style` so it cannot be accidentally overridden
                // by a caller-supplied `paddingTop`.
                ...(isReversed && bottomPinPaddingTop > 0 ? {
                    paddingTop: bottomPinPaddingTop,
                } : {}),
                // During measurement phase, hide items off-screen to measure without visual flash
                ...(isMeasuringForInitialIndex ? {
                    position: 'relative',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                } : {}),
            }}
        >
            {/* Sentinel for onReachedEnd in reversed mode — sits at the top
                (the load-more-older edge) so it fires when the user scrolls up. */}
            {onReachedEnd && isReversed && (
                <div ref={reachedEndSentinelRef} style={{ height: 0 }} aria-hidden="true" />
            )}

            {/* Top spacer */}
            {topSpacerHeight > 0 && (
                <div
                    style={{
                        height: topSpacerHeight,
                        flexShrink: 0,
                    }}
                    aria-hidden="true"
                />
            )}

            {isGridMode ? (
                // Grid mode: group items into fixed-height rows
                (() => {
                    const visibleItems = items.slice(renderStartItem, renderEndItem);
                    const rows: Array<{ rowIndex: number; rowItems: Array<{ item: T; itemIndex: number }> }> = [];
                    let currentRow: { rowIndex: number; rowItems: Array<{ item: T; itemIndex: number }> } | null = null;

                    visibleItems.forEach((item, relIdx) => {
                        const itemIndex = renderStartItem + relIdx;
                        const rowIndex = Math.floor(itemIndex / columnsCount);
                        if (!currentRow || currentRow.rowIndex !== rowIndex) {
                            currentRow = { rowIndex, rowItems: [] };
                            rows.push(currentRow);
                        }
                        currentRow.rowItems.push({ item, itemIndex });
                    });

                    return rows.map(({ rowIndex, rowItems }) => (
                        <div
                            key={`row-${rowIndex}`}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: `${crossAxisSpacing}px`,
                                height: `${gridRowHeight}px`,
                                marginBottom: rowIndex < rowCount - 1 ? `${mainAxisSpacing}px` : undefined,
                            }}
                        >
                            {rowItems.map(({ item, itemIndex }) => {
                                const key = getItemKey(item, itemIndex);
                                return (
                                    <div
                                        key={key}
                                        data-key={key}
                                        data-index={itemIndex}
                                        style={{ flex: 1, minWidth: 0, display: 'grid', overflow: 'hidden' }}
                                    >
                                        {renderItem(item, itemIndex)}
                                    </div>
                                );
                            })}
                            {/* Fill empty slots in the last row to maintain consistent column widths */}
                            {rowItems.length < columnsCount &&
                                Array.from({ length: columnsCount - rowItems.length }).map((_, i) => (
                                    <div key={`empty-${i}`} style={{ flex: 1, minWidth: 0 }} aria-hidden="true" />
                                ))
                            }
                        </div>
                    ));
                })()
            ) : (
                // List mode: flat list with marginBottom gap
                items.slice(visibleRange.start, visibleRange.end).map((item, relativeIndex) => {
                    const absoluteIndex = visibleRange.start + relativeIndex;
                    const key = getItemKey(item, absoluteIndex);

                    return (
                        <div
                            key={key}
                            data-key={key}
                            data-index={absoluteIndex}
                            ref={(el) => setItemRef(key, el)}
                            style={{
                                marginBottom: direction === 'vertical' && gap > 0 ? `${gap}px` : undefined,
                                marginRight: direction === 'horizontal' && gap > 0 ? `${gap}px` : undefined,
                            }}
                        >
                            {renderItem(item, absoluteIndex)}
                        </div>
                    );
                })
            )}

            {/* Bottom spacer */}
            {bottomSpacerHeight > 0 && (
                <div
                    style={{
                        height: bottomSpacerHeight,
                        flexShrink: 0,
                    }}
                    aria-hidden="true"
                />
            )}

            {/* Sentinel for onReachedEnd in default (top-anchored) mode — sits
                at the bottom so it fires when the user scrolls to the end. */}
            {onReachedEnd && !isReversed && (
                <div ref={reachedEndSentinelRef} style={{ height: 0 }} aria-hidden="true" />
            )}
        </div>
    );
}) as <T>(props: VirtualListProps<T> & { ref?: React.Ref<VirtualListHandle> }) => React.ReactElement;
